import { useCallback, useEffect, useRef, useState } from 'react'
import { PhoneIncoming, RefreshCw, Settings2, FlaskConical, PhoneForwarded, Moon, ArrowRight } from 'lucide-react'
import { Link } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { apiRead, usePermissions } from '../admin/primitives.js'
import {
  cheminFiche, dateTarifs, dollars, duree, etatBudget, etatSacha, fusionnerAppels, heureParis, libelleDecroche, libelleEquipe, libelleType, messageErreur,
  quandParis, soireeLueA, type AppelResume, type Tableau,
} from './interphone-model.js'

/**
 * SÉCURITÉ › INTERPHONE IA (`/securite/interphone-ia`, 22/10/2026).
 *
 * L'état de Sacha, les compteurs du jour et le journal des appels de
 * l'interphone de la porte du personnel. Tout est lu EN DIRECT chez le
 * service Interphone, par le relais de Gestion : rien n'est gardé ici. Le
 * service muet se dit en gris, une seule fois, jamais avec des zéros inventés.
 *
 * « Actualiser » et « Appels plus anciens » ne se marchent pas dessus : une
 * page plus ancienne arrivée après une actualisation est ignorée, et le
 * journal est dédoublonné par identifiant.
 *
 * Utilisable à 390 px : chaque appel est une ligne-carte qui passe sur deux
 * lignes, sans tableau à défiler de côté.
 */

type Lecture<T> = { etat: 'chargement' } | { etat: 'ok'; data: T } | { etat: 'erreur'; status: number; code: string | null; message: string }
type Journal = { appels: AppelResume[]; suite: string | null }

const codeDe = (d: unknown): string | null => {
  const e = typeof d === 'object' && d !== null ? (d as { error?: unknown }).error : null
  return typeof e === 'string' ? e : null
}

export function InterphonePage() {
  const { permissions } = usePermissions()
  // Les réglages se LISENT avec interphone.read ; les modifier demande interphone.manage (la page le dit).
  const voitReglages = permissions.includes('interphone.read')
  const peutRaccorder = permissions.includes('interphone.credentials.manage') || permissions.includes('integration.read')
  const [tableau, setTableau] = useState<Lecture<Tableau>>({ etat: 'chargement' })
  const [appels, setAppels] = useState<Lecture<Journal>>({ etat: 'chargement' })
  const [actualisation, setActualisation] = useState(false)
  const [plus, setPlus] = useState(false)
  const [erreurPlus, setErreurPlus] = useState<string | null>(null)
  // Chaque actualisation ouvre une GÉNÉRATION : une page d'une génération passée est ignorée.
  const generation = useRef(0)

  const charger = useCallback(async () => {
    const g = ++generation.current
    setErreurPlus(null); setPlus(false); setActualisation(true)
    const lire = async <T,>(url: string, repli: string): Promise<Lecture<T>> => {
      try {
        const r = await apiRead<T>(url)
        return r.status === 200 ? { etat: 'ok', data: r.data } : { etat: 'erreur', status: r.status, code: codeDe(r.data), message: messageErreur(r.status, r.data, repli) }
      } catch { return { etat: 'erreur', status: 0, code: null, message: 'Gestion n’a pas répondu.' } }
    }
    const [t, j] = await Promise.all([
      lire<Tableau>('/api/v1/interphone/tableau', 'Le tableau de l’interphone est indisponible.'),
      lire<Journal>('/api/v1/interphone/appels?limite=30', 'Le journal des appels est indisponible.'),
    ])
    if (g !== generation.current) return
    setTableau(t)
    setAppels(j.etat === 'ok' ? { etat: 'ok', data: { appels: fusionnerAppels([], Array.isArray(j.data.appels) ? j.data.appels : []), suite: j.data.suite ?? null } } : j)
    setActualisation(false)
  }, [])
  useEffect(() => { void charger() }, [charger])

  const chargerPlus = async () => {
    if (appels.etat !== 'ok' || !appels.data.suite || plus) return
    const g = generation.current
    setPlus(true); setErreurPlus(null)
    try {
      const r = await apiRead<Journal>(`/api/v1/interphone/appels?limite=30&avant=${encodeURIComponent(appels.data.suite)}`)
      if (g !== generation.current) return
      if (r.status === 200) {
        const page = Array.isArray(r.data.appels) ? r.data.appels : []
        setAppels((a) => a.etat === 'ok' ? { etat: 'ok', data: { appels: fusionnerAppels(a.data.appels, page), suite: r.data.suite ?? null } } : a)
      } else setErreurPlus(messageErreur(r.status, r.data, 'La suite du journal est indisponible.'))
    } catch {
      if (g === generation.current) setErreurPlus('Gestion n’a pas répondu.')
    } finally {
      if (g === generation.current) setPlus(false)
    }
  }

  const nonRaccorde = tableau.etat === 'erreur' && tableau.code === 'INTERPHONE_NOT_CONFIGURED'
  // La même panne ne s'écrit qu'une fois : le panneau d'état la dit, le journal s'efface.
  const memePanne = tableau.etat === 'erreur' && appels.etat === 'erreur' && tableau.code !== null && tableau.code === appels.code

  return (
    <main className="lc-page lc-page--management lc-stagger ip-page">
      <Trail />
      <header className="ip-hero">
        <div className="ip-hero__texte">
          <h1 className="lc-title"><PhoneIncoming size={26} aria-hidden="true" /> Interphone IA</h1>
          <p className="lc-subtitle">
            Sacha répond à l’interphone de la porte du personnel et prévient la bonne équipe.
            Le journal et les enregistrements restent dans le service Interphone.
          </p>
        </div>
        <div className="ip-hero__actions">
          <button type="button" className="lc-btn lc-btn--ghost" disabled={actualisation} aria-busy={actualisation} onClick={() => { void charger() }}>
            <RefreshCw size={15} aria-hidden="true" /> {actualisation ? 'Actualisation…' : 'Actualiser'}
          </button>
          {voitReglages ? <Link href="/settings/interphone" className="lc-btn"><Settings2 size={15} aria-hidden="true" /> Réglages</Link> : null}
        </div>
      </header>

      <EtatPanneau lecture={tableau} />
      <ConsoPanneau lecture={tableau} voitReglages={voitReglages} />
      {nonRaccorde && peutRaccorder ? (
        <p className="ip-note">Le raccordement se règle dans <Link href="/settings/integrations/interphone" className="ip-lien">Intégrations › Interphone</Link>.</p>
      ) : null}

      <section className="lc-panel ip-section" aria-labelledby="ip-journal">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="ip-journal">Journal des appels</h2>
          <span className="lc-hint">heures de Paris · texte gardé 6 mois, voix 30 jours</span>
        </header>
        <div className="lc-panel__body">
          {appels.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Chargement du journal…</p>
            : appels.etat === 'erreur' ? (memePanne ? <p className="lc-hint">Journal indisponible pour la même raison.</p> : <p className="ip-gris" role="status">{appels.message}</p>)
              : appels.data.appels.length === 0 ? <p className="lc-hint">Aucun appel enregistré pour l’instant.</p>
                : (
                  <>
                    <ul className="ip-journal">
                      {appels.data.appels.map((a) => <LigneAppel key={a.id} a={a} />)}
                    </ul>
                    {appels.data.suite ? (
                      <div className="ip-suite">
                        <button type="button" className="lc-btn" disabled={plus || actualisation} onClick={() => { void chargerPlus() }}>
                          {plus ? 'Chargement…' : 'Appels plus anciens'}
                        </button>
                        {erreurPlus ? <p className="ip-gris" role="status">{erreurPlus}</p> : null}
                      </div>
                    ) : null}
                  </>
                )}
        </div>
      </section>
    </main>
  )
}

const nombre = (v: unknown): string => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '—')

function EtatPanneau({ lecture }: { lecture: Lecture<Tableau> }) {
  if (lecture.etat === 'chargement') {
    return <section className="lc-panel ip-section"><div className="lc-panel__body"><p className="lc-hint" aria-busy="true">Lecture de l’état de Sacha…</p></div></section>
  }
  if (lecture.etat === 'erreur') {
    return <section className="lc-panel ip-section"><div className="lc-panel__body"><p className="ip-gris" role="status">{lecture.message}</p></div></section>
  }
  // Forme gardée : un champ absent s'affiche « — », jamais une erreur d'écran.
  const t = lecture.data
  const etat = etatSacha({ agent: t?.agent ?? null })
  const cases = [
    { cle: 'appels', libelle: 'Appels aujourd’hui', valeur: nombre(t?.aujourdhui?.appels) },
    { cle: 'prevenus', libelle: 'Équipe prévenue', valeur: nombre(t?.aujourdhui?.prevenus) },
    { cle: 'secours', libelle: 'Transférés au secours', valeur: nombre(t?.aujourdhui?.secours) },
  ]
  const soiree = t?.soiree ?? null
  const lue = soireeLueA(soiree?.lu_a)
  const dernier = t?.dernier_appel ?? null
  return (
    <section className="lc-panel ip-section" aria-labelledby="ip-etat">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="ip-etat">Sacha</h2>
        <span className={`lc-pill ${etat.ton === 'ok' ? 'lc-pill--ok' : etat.ton === 'ko' ? 'lc-pill--error' : 'lc-pill--idle'}`}>
          <span className={`lc-dot ${etat.ton === 'ok' ? 'lc-dot--ok' : etat.ton === 'ko' ? 'lc-dot--error' : ''}`} aria-hidden="true" />
          {etat.texte}
        </span>
      </header>
      <div className="lc-panel__body ip-etat">
        {t?.mode_test === true ? (
          <p className="ip-alerte" role="status">
            <FlaskConical size={16} aria-hidden="true" />
            <span><strong>Mode test</strong> — Sacha répond mais n’appelle personne : tout est seulement noté.</span>
          </p>
        ) : null}
        <ul className="ip-compteurs" aria-label="Compteurs du jour">
          {cases.map((c) => (
            <li key={c.cle} className="ip-compteur">
              <span className="ip-compteur__valeur" aria-describedby={`ip-cpt-${c.cle}`}>{c.valeur}</span>
              <span className="ip-compteur__libelle" id={`ip-cpt-${c.cle}`}>{c.libelle}</span>
            </li>
          ))}
        </ul>
        <dl className="ip-faits">
          <div>
            <dt>Soirée annoncée</dt>
            <dd>
              {soiree?.nom
                ? `${soiree.nom}${soiree.ouverture ? `, ouverture ${heureParis(soiree.ouverture)}` : ''}${lue ? ` (${lue})` : ''}`
                : 'Aucune soirée ce soir (Sacha utilise les infos manuelles).'}
            </dd>
          </div>
          <div>
            <dt>Dernier appel</dt>
            <dd>
              {dernier?.id ? (
                <Link href={cheminFiche(dernier.id)} className="ip-lien">
                  {quandParis(dernier.debut)} — {dernier.resume || libelleType(dernier.type ?? '')}
                </Link>
              ) : 'Aucun.'}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  )
}

/**
 * CONSOMMATION (23/10/2026) : ce que l'IA a coûté aujourd'hui et ce mois-ci (Claude pour réfléchir, Mistral pour écouter et
 * parler), estimé en dollars d'après les grilles publiques, et le budget du mois. Même lecture que l'état de Sacha : en
 * chargement ou en panne, rien ici (la panne est déjà dite au-dessus) ; un service plus ancien sans ces chiffres, rien non plus.
 */
function ConsoPanneau({ lecture, voitReglages }: { lecture: Lecture<Tableau>; voitReglages: boolean }) {
  if (lecture.etat !== 'ok' || !lecture.data?.conso?.mois) return null
  const c = lecture.data.conso
  const budget = etatBudget(c)
  const mesure = c.mois.appels > 0
  const cases = [
    { cle: 'jour', libelle: 'Aujourd’hui', valeur: c.aujourdhui?.appels ? dollars(c.aujourdhui.total_usd) : '—' },
    { cle: 'mois', libelle: 'Ce mois-ci', valeur: mesure ? dollars(c.mois.total_usd) : '—' },
    { cle: 'refus', libelle: 'Refus « trop de requêtes » ce mois-ci', valeur: mesure ? nombre(c.mois.refus_429) : '—' },
  ]
  const largeur = budget.ratio !== null && Number.isFinite(budget.ratio) ? `${Math.round(Math.max(0, Math.min(1, budget.ratio)) * 100)}%` : null
  return (
    <section className="lc-panel ip-section" aria-labelledby="ip-conso">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="ip-conso">Consommation de l’IA</h2>
        <span className={`lc-pill lc-pill--${budget.ton}`}>{budget.texte}</span>
      </header>
      <div className="lc-panel__body ip-etat">
        <ul className="ip-compteurs" aria-label="Coût estimé">
          {cases.map((k) => (
            <li key={k.cle} className="ip-compteur">
              <span className="ip-compteur__valeur" aria-describedby={`ip-conso-${k.cle}`}>{k.valeur}</span>
              <span className="ip-compteur__libelle" id={`ip-conso-${k.cle}`}>{k.libelle}</span>
            </li>
          ))}
        </ul>
        {c.budget_mensuel_usd > 0 ? (
          <div className="ip-budget">
            <p className="ip-budget__chiffre"><strong>{mesure ? dollars(c.mois.total_usd) : '—'}</strong> / {dollars(c.budget_mensuel_usd)} de budget ce mois-ci</p>
            {largeur !== null ? (
              <span className="ip-jauge" aria-hidden="true">
                <span className={`ip-jauge__plein ip-jauge__plein--${budget.ton}`} style={{ width: largeur }} />
              </span>
            ) : null}
          </div>
        ) : null}
        <dl className="ip-faits">
          <div>
            <dt>Détail du mois</dt>
            <dd>{mesure ? `Claude ${dollars(c.mois.claude_usd)} · Mistral ${dollars(c.mois.mistral_usd)} · ${c.mois.appels} appel${c.mois.appels > 1 ? 's' : ''} mesuré${c.mois.appels > 1 ? 's' : ''}` : 'Aucun appel mesuré pour l’instant.'}</dd>
          </div>
          <div>
            <dt>Estimation</dt>
            <dd>D’après les tarifs publics relevés le {dateTarifs(c.tarifs)} ; la facture des fournisseurs fait foi.</dd>
          </div>
        </dl>
        {voitReglages ? (
          <Link className="lc-hint ip-budget__lien" href="/settings/interphone">Régler le budget <ArrowRight size={13} aria-hidden="true" /></Link>
        ) : null}
      </div>
    </section>
  )
}

function LigneAppel({ a }: { a: AppelResume }) {
  return (
    <li>
      <Link href={cheminFiche(a.id)} className="ip-appel">
        <span className="ip-appel__heure">{quandParis(a.debut)}</span>
        <span className="ip-appel__corps">
          <span className="ip-appel__resume">{a.resume || libelleType(a.type)}</span>
          <span className="ip-appel__meta">
            {a.origine === 'secours'
              ? <span className="lc-pill lc-pill--warning"><PhoneForwarded size={12} aria-hidden="true" /> Secours</span>
              : a.origine === 'repertoire'
                ? <span className="lc-pill lc-pill--idle">Message Répertoire</span>
                : <span className="lc-pill lc-pill--idle">Sacha</span>}
            <span>{libelleType(a.type)}</span>
            {a.equipe ? <span>· {libelleEquipe(a.equipe)}</span> : null}
            <span>· {duree(a.duree_s)}</span>
            {a.suite ? <span className={a.suite.etat === 'ouvert' ? '' : 'ip-appel__manque'}>· {a.suite.texte}</span>
              : a.decroche === true || a.decroche === false ? <span className={a.decroche ? '' : 'ip-appel__manque'}>· {libelleDecroche(a.decroche)}</span> : null}
            {a.club_ferme ? <span className="ip-appel__ferme"><Moon size={12} aria-hidden="true" /> club fermé</span> : null}
          </span>
        </span>
      </Link>
    </li>
  )
}
